import {
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useAuth, type TenantChoice } from '../auth/AuthProvider';
import { groupTenantChoices } from './tenantGroups';

/**
 * Switch the active tenant. Data is tenant-scoped, so this re-keys every query.
 *
 * Grouped by CUSTOMER once a hierarchy exists. A flat list was right when a
 * person belonged to unrelated tenants; after the migration it showed a tenant
 * administrator "Halewood Transport" and "Default" side by side as peers, which describes
 * neither — one is the organisation, the other is where its fleet actually is.
 *
 * For everybody else the grouping never appears: a sub-tenant user has a single
 * entry and no parent, so this renders exactly as it did before (D5).
 */
export function TenantSelector() {
  const { tenantChoices, tenantId, setTenant } = useAuth();
  if (tenantChoices.length === 0) return null;

  const groups = groupTenantChoices(tenantChoices);
  const grouped = groups.some((g) => g.parent !== null && g.children.length > 0);
  const onChange = (e: SelectChangeEvent) => setTenant(e.target.value);

  /*
   * A tenant that is not active is shown, and not enterable.
   *
   * Entering one gains nothing — every request inside is refused, and the action
   * that fixes it (reactivating) lives on the organisation's Sub-tenants page,
   * not in the branch. It stays listed because an administrator who suspended a
   * branch needs to see that it is still there; a switcher that quietly dropped
   * it would read as the branch having been deleted.
   */
  const stateNote = (c: TenantChoice) =>
    c.state === 'SUSPENDED' ? 'Suspended' : c.state === 'ARCHIVED' ? 'Archived' : null;

  return (
    <Select
      size="small"
      value={tenantId ?? ''}
      onChange={onChange}
      inputProps={{ 'aria-label': 'Active tenant' }}
      startAdornment={
        <ApartmentOutlinedIcon fontSize="small" sx={{ mr: 0.75, color: 'text.secondary' }} />
      }
      sx={{
        minWidth: 190,
        height: 34,
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
      }}
      disabled={tenantChoices.length === 1}
      // The row renders two lines for a parent; the closed control must not.
      renderValue={(value) => tenantChoices.find((c) => c.tenantId === value)?.tenantName ?? ''}
    >
      {groups.flatMap((g) => {
        const items = [];
        if (g.parent) {
          items.push(
            <MenuItem
              key={g.parent.tenantId}
              value={g.parent.tenantId}
              disabled={g.parent.state !== 'ACTIVE'}
            >
              <Stack>
                <Typography variant="body2">{g.parent.tenantName}</Typography>
                {/* Said out loud, because a parent holds no vehicles and no
                    incidents: a screenful of empty tables is otherwise
                    indistinguishable from something being broken. */}
                <Typography variant="caption" color="text.secondary">
                  {stateNote(g.parent) ?? 'Organisation settings'}
                </Typography>
              </Stack>
            </MenuItem>,
          );
          if (grouped && g.children.length > 0) {
            items.push(
              <ListSubheader key={`${g.parent.tenantId}-sub`} sx={{ lineHeight: '28px' }}>
                Sub-tenants
              </ListSubheader>,
            );
          }
        }
        for (const c of g.children) {
          items.push(
            <MenuItem
              key={c.tenantId}
              value={c.tenantId}
              disabled={c.state !== 'ACTIVE'}
              // A real indent. `pl: 3` was 8px more than the default padding,
              // which on screen was indistinguishable from no indent at all —
              // the branches read as peers of the organisation above them.
              sx={grouped ? { pl: 4.5 } : undefined}
            >
              <Stack>
                <Typography variant="body2">{c.tenantName}</Typography>
                {stateNote(c) && (
                  <Typography variant="caption" color="text.secondary">
                    {stateNote(c)}
                  </Typography>
                )}
              </Stack>
            </MenuItem>,
          );
        }
        return items;
      })}
    </Select>
  );
}
